import {
  Button,
  DateRangePicker,
  MultiSelect,
} from "~/ui-rtk/components/ui/controls"
import { isValidDate } from "~/ui-rtk/hooks/period"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"
import HighlightToggle from "~/ui-rtk/components/ui/controls/HighlightToggle/HighlightToggle"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import { Card, RichTooltip } from "~/ui-rtk/components/ui/common"
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import {
  InformationCircleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline"
import BetaIcon from "~/ui-rtk/components/ui/common/BetaIcon"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { SOURCES_MAP } from "~/ui-rtk/constants/sources"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import { useSelectFilterOptions } from "../hooks/useSelectFilterOptions"

interface ControlsContainerProps {
  isLoading: boolean
  sourcesOptions: Array<{
    value: string
    label: string
    isBeta?: boolean
  }>
  sourceKeys: string[]
  setSourceKeys: (keys: string[]) => void
  highlight: boolean
  setHighlight: (highlight: boolean) => void
  amazonEnabled?: boolean
  toggleAmazon?: () => void
  columnOptions: TMultiSelectOption[]
  columnKeys: string[]
  setColumnKeys: (keys: string[]) => void
  dateRange: [Date?, Date?]
  lastDateRestriction: Date
  onDateChange: (dates: { start: Date; end: Date }) => void
  chartProps?: {
    highlightCells?: boolean
  }
  columnConfigFiltered: TableVisualizationColumn[]
  brandMediaId: string
  lastDate?: string
  isLastUpdateDateLoading: boolean
  onAutoClassify?: () => void
  isClassifying?: boolean
  hasUnassignedAdsets?: boolean
}

export function ControlsContainer({
  isLoading,
  sourcesOptions,
  sourceKeys,
  setSourceKeys,
  highlight,
  setHighlight,
  amazonEnabled,
  toggleAmazon,
  columnOptions,
  columnKeys,
  setColumnKeys,
  dateRange,
  lastDateRestriction,
  onDateChange,
  chartProps,
  columnConfigFiltered,
  brandMediaId,
  lastDate,
  isLastUpdateDateLoading,
  onAutoClassify,
  isClassifying,
  hasUnassignedAdsets,
}: ControlsContainerProps) {
  const { getSelectOptions } = useSelectFilterOptions()

  return (
    <div className="flex justify-between items-start gap-system-md mb-system-lg">
      <div className={cn({ "opacity-50": isLoading })}>
        <div className="join">
          {sourcesOptions.map(({ value, label, isBeta }) => (
            <Button
              variant={{ variant: "solid", color: "blue" }}
              key={value}
              title={label}
              className={cn(
                `join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm  rounded-system-md relative`,
                { "bg-system-blue-800": !sourceKeys.includes(value) },
                {
                  "bg-system-blue-600":
                    sourceKeys.length === 0 || sourceKeys.includes(value),
                },
              )}
              onClick={() =>
                setSourceKeys(
                  sourceKeys.includes(value)
                    ? sourceKeys.filter(sourceId => sourceId !== value)
                    : [...sourceKeys, value],
                )
              }
            >
              {SOURCES_MAP[value?.toUpperCase() as keyof typeof SOURCES_MAP]
                ?.icon && (
                <img
                  src={
                    SOURCES_MAP[
                      value?.toUpperCase() as keyof typeof SOURCES_MAP
                    ].icon
                  }
                  alt={label}
                  className="w-4 h-4"
                />
              )}
              <span
                className={cn("font-[Lato] ", {
                  "text-base text-system-gray-300 font-normal":
                    !sourceKeys.includes(value),
                  "text-white font-semibold":
                    sourceKeys.length === 0 || sourceKeys.includes(value),
                })}
              >
                {label}
              </span>
              {isBeta && <BetaIcon />}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex justify-end items-stretch gap-system-sm">
        {chartProps?.highlightCells && (
          <HighlightToggle
            isEnabled={highlight}
            toggle={() => setHighlight(!highlight)}
          />
        )}
        {toggleAmazon && (
          <AmazonToggle
            amazonEnabled={amazonEnabled}
            toggleAmazon={toggleAmazon}
          />
        )}

        {onAutoClassify && (
          <Button
            variant={{ variant: "solid", color: "blue" }}
            className="border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md bg-system-blue-700 hover:bg-system-blue-600 flex items-center gap-system-xs"
            onClick={onAutoClassify}
            disabled={isClassifying || !hasUnassignedAdsets}
          >
            {isClassifying ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <SparklesIcon className="w-4 h-4" />
            )}
            <span className="font-[Lato] text-white font-semibold">
              Auto-Generate
            </span>
          </Button>
        )}

        <MultiSelect
          label="Columns"
          options={columnOptions}
          value={columnOptions.filter((option: TMultiSelectOption) =>
            columnKeys.includes(option.value),
          )}
          onChange={(options: TMultiSelectOption[]) =>
            setColumnKeys(options.map(({ value }) => value))
          }
        />
        <TableFiltersBar
          id={brandMediaId}
          columnConfig={columnConfigFiltered}
          getSelectOptions={getSelectOptions}
        />
        {dateRange?.[0] &&
          dateRange?.[1] &&
          isValidDate(dateRange[0]) &&
          isValidDate(dateRange[1]) && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              comparisonEnabled={false}
              maxDate={lastDateRestriction}
              onChange={onDateChange}
            />
          )}
        <RichTooltip
          content={
            <LastUpdatedChip
              usedInTooltip={true}
              lastDate={lastDate}
              isLoading={isLastUpdateDateLoading}
            >
              Max date available in Page:
            </LastUpdatedChip>
          }
        >
          <Card className="p-system-xs relative h-system-2xl w-system-2xl flex items-center justify-center rounded-system-md bg-system-blue-700 border border-system-blue-600 cursor-pointer">
            <span className="absolute -top-system-3xs -right-system-3xs flex size-system-sm">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-system-brand-pink-400 opacity-75"></span>
              <span className="relative inline-flex size-system-sm rounded-full bg-system-brand-pink-500"></span>
            </span>
            <InformationCircleIcon className="w-system-lg h-system-lg" />
          </Card>
        </RichTooltip>
      </div>
    </div>
  )
}

export default ControlsContainer
